import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Activity } from 'lucide-react';
import { API_URL, API_KEY } from '../config';
import { useAuth } from '../contexts/AuthContext';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');

  :root {
    --teal:        #0EA5A0;
    --teal-d:      #0B8A85;
    --teal-dim:    rgba(14,165,160,0.10);
    --sky:         #38BDF8;
    --navy:        #0F2744;
    --bg:          #F4F8FB;
    --bg2:         #EBF2F8;
    --white:       #FFFFFF;
    --border:      rgba(14,165,160,0.18);
    --border2:     rgba(14,165,160,0.28);
    --text1:       #0F2744;
    --text2:       #476585;
    --text3:       #7A9AB5;
    --err:         #DC2626;
    --err-bg:      rgba(220,38,38,0.07);
    --suc:         #059669;
    --suc-bg:      rgba(5,150,105,0.08);
    --shadow:      0 4px 24px rgba(14,165,160,0.10), 0 1px 4px rgba(15,39,68,0.08);
    --shadow-btn:  0 4px 18px rgba(14,165,160,0.35);
    --radius:      18px;
    --radius-sm:   8px;
    --fd:          'DM Serif Display', Georgia, serif;
    --fb:          'DM Sans', system-ui, sans-serif;
    --transition:  all 0.22s cubic-bezier(0.4,0,0.2,1);
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: var(--fb); background: var(--bg); color: var(--text1); -webkit-font-smoothing: antialiased; }

  .auth-root {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1fr 1fr;
    background: linear-gradient(135deg, #EBF5FB 0%, #F4F8FB 40%, #EDF7F4 100%);
    position: relative;
  }

  .auth-root::before {
    content: '';
    position: fixed; inset: 0;
    background-image: radial-gradient(rgba(14,165,160,0.11) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none; z-index: 0;
  }

  .panel-left {
    position: relative; z-index: 1;
    padding: 56px 48px;
    display: flex; flex-direction: column; justify-content: center;
    border-right: 1px solid var(--border);
    background: linear-gradient(160deg, rgba(14,165,160,0.07) 0%, transparent 60%);
  }

  .logo-wrap { margin-bottom: 52px; }
  .logo-wrap img { height: 38px; width: auto; display: block; }

  .logo-fallback {
    display: flex; align-items: center; gap: 10px;
  }
  .logo-icon {
    width: 38px; height: 38px; border-radius: 10px;
    background: var(--teal);
    display: flex; align-items: center; justify-content: center;
  }
  .logo-name { font-family: var(--fd); font-size: 1.2rem; color: var(--navy); }

  .panel-tagline {
    font-family: var(--fd);
    font-size: clamp(1.75rem, 2.8vw, 2.4rem);
    line-height: 1.2; color: var(--navy); margin-bottom: 16px;
  }
  .panel-tagline em { font-style: italic; color: var(--teal); }

  .panel-sub {
    font-size: 0.9rem; line-height: 1.75;
    color: var(--text2); max-width: 340px; margin-bottom: 44px;
  }

  .feat-list { display: flex; flex-direction: column; gap: 12px; }
  .feat-pill {
    display: inline-flex; align-items: center; gap: 10px;
    background: var(--white); border: 1px solid var(--border2);
    border-radius: 9px; padding: 9px 14px;
    box-shadow: 0 2px 8px rgba(14,165,160,0.1);
  }
  .feat-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); flex-shrink: 0; }
  .feat-txt { font-size: 0.82rem; font-weight: 500; color: var(--text1); }

  .panel-right {
    position: relative; z-index: 1;
    display: flex; align-items: center; justify-content: center;
    padding: 48px 32px;
  }

  .form-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 44px 40px;
    width: 100%; max-width: 380px;
    box-shadow: var(--shadow);
    animation: slideUp 0.5s cubic-bezier(0.4,0,0.2,1) both;
  }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .mobile-logo { display: none; margin-bottom: 26px; }
  .mobile-logo img { height: 28px; width: auto; }

  .badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--suc-bg); border: 1px solid rgba(5,150,105,0.2);
    border-radius: 100px; padding: 4px 12px;
    font-size: 0.7rem; font-weight: 500; color: var(--suc);
    margin-bottom: 22px; letter-spacing: 0.03em;
  }
  .pulse-dot {
    width: 5px; height: 5px; border-radius: 50%;
    background: var(--suc); animation: pulse 2s infinite;
  }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }

  .form-title { font-family: var(--fd); font-size: 1.7rem; color: var(--navy); margin-bottom: 6px; }
  .form-subtitle { font-size: 0.84rem; color: var(--text2); margin-bottom: 26px; }

  .alert {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 12px 15px; border-radius: var(--radius-sm);
    font-size: 0.8125rem; margin-bottom: 18px; line-height: 1.5;
  }
  .alert-error { background: var(--err-bg); border: 1px solid rgba(220,38,38,0.2); color: var(--err); }

  .field { margin-bottom: 15px; }
  .field-label {
    display: block;
    font-size: 0.69rem; font-weight: 600;
    letter-spacing: 0.07em; text-transform: uppercase;
    color: var(--text3); margin-bottom: 6px;
  }
  .field-wrap { position: relative; }
  .field-input {
    width: 100%; background: #F8FAFC; border: 1px solid #D4E4EF;
    border-radius: var(--radius-sm); padding: 11px 13px;
    font-family: var(--fb); font-size: 0.9rem; color: var(--text1);
    outline: none; transition: var(--transition); appearance: none;
  }
  .field-input::placeholder { color: #A8C0D0; }
  .field-input:focus { border-color: var(--teal); background: #fff; box-shadow: 0 0 0 3px rgba(14,165,160,0.14); }
  .field-input.has-icon { padding-right: 44px; }
  .field-icon-btn {
    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
    background: none; border: none; color: var(--text3); cursor: pointer;
    display: flex; align-items: center; padding: 2px; transition: color 0.15s;
  }
  .field-icon-btn:hover { color: var(--teal); }

  .forgot-link {
    float: right; font-size: 0.8rem; color: var(--teal);
    background: none; border: none; cursor: pointer;
    font-family: var(--fb); padding: 0; margin-top: 7px; margin-bottom: 20px;
    transition: opacity 0.15s;
  }
  .forgot-link:hover { opacity: 0.75; }

  .btn-primary {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    width: 100%; padding: 13px;
    background: var(--teal); border: none; border-radius: var(--radius-sm);
    font-family: var(--fb); font-size: 0.9375rem; font-weight: 600; color: #fff;
    cursor: pointer; box-shadow: var(--shadow-btn);
    transition: var(--transition); letter-spacing: 0.01em;
  }
  .btn-primary:hover:not(:disabled) { background: var(--teal-d); transform: translateY(-1px); box-shadow: 0 6px 24px rgba(14,165,160,0.45); }
  .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

  .divider { display: flex; align-items: center; gap: 12px; margin: 20px 0; color: var(--text3); font-size: 0.72rem; }
  .divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: #E2EDF5; }

  .footer-text { text-align: center; font-size: 0.84rem; color: var(--text2); }
  .btn-link { background: none; border: none; color: var(--teal); cursor: pointer; font-size: inherit; font-family: inherit; padding: 0; font-weight: 500; }

  .spinner { display: inline-block; width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  @media (max-width: 860px) {
    .auth-root { grid-template-columns: 1fr; }
    .panel-left { display: none; }
    .panel-right { padding: 40px 20px; }
    .mobile-logo { display: block; }
    .form-card { padding: 36px 28px; }
  }
`;

export default function LoginScreen() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) { setError('Both username and password are required.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (res.ok) { login(username.toLowerCase(), data.email); navigate('/dashboard'); }
      else setError(data.message || 'Invalid username or password.');
    } catch { setError('Cannot connect to server. Please check your connection.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{css}</style>
      <div className="auth-root">
        <div className="panel-left">
          <div className="logo-wrap">
            <img src="/logo.png" alt="Cytometrix"
              onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
            <div className="logo-fallback" style={{ display: 'none' }}>
              <div className="logo-icon"><Activity size={20} color="#fff" strokeWidth={2.5} /></div>
              <span className="logo-name">Cytometrix</span>
            </div>
          </div>
          <h1 className="panel-tagline">Clinical-Grade<br /><em>Wound Analysis</em></h1>
          <p className="panel-sub">AI-powered imaging analysis for real-time wound measurement, tissue classification, and healing tracking.</p>
          <div className="feat-list">
            {['FDA-Aligned Design', 'ISO/IEC SaMD Standards', 'HIPAA-Ready Architecture'].map(f => (
              <div key={f}><div className="feat-pill"><span className="feat-dot" /><span className="feat-txt">{f}</span></div></div>
            ))}
          </div>
        </div>

        <div className="panel-right">
          <div className="form-card">
            <div className="mobile-logo">
              <img src="/logo.png" alt="Cytometrix" onError={e => e.target.style.display='none'} />
            </div>
            <div className="badge"><span className="pulse-dot" />Secure Clinical Session</div>
            <h2 className="form-title">Welcome back</h2>
            <p className="form-subtitle">Sign in to your clinical account</p>

            {error && (
              <div className="alert alert-error">
                <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />{error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div className="field">
                <label className="field-label">Username</label>
                <div className="field-wrap">
                  <input className="field-input" type="text" value={username}
                    onChange={e => setUsername(e.target.value)} placeholder="Enter your username" autoComplete="username" />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Password</label>
                <div className="field-wrap">
                  <input className="field-input has-icon" type={showPw ? 'text' : 'password'}
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password" autoComplete="current-password" />
                  <button type="button" className="field-icon-btn" onClick={() => setShowPw(!showPw)} tabIndex={-1}>
                    {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
              <div style={{ overflow: 'hidden', marginTop: -4, marginBottom: 20 }}>
                <button type="button" className="forgot-link" onClick={() => navigate('/forgot-password')}>Forgot password?</button>
              </div>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Sign In'}
              </button>
            </form>

            <div className="divider">or</div>
            <p className="footer-text">No account?{' '}
              <button className="btn-link" onClick={() => navigate('/signup')}>Create one</button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
